import GetPetVetLogo from "@/components/brand/GetPetVetLogo";
import { useLogoSizes, useNavLogoHeight } from "@/contexts/LogoSizeContext";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import NotificationBell from "@/components/NotificationBell";
import MessageBell from "@/components/MessageBell";
import type { LucideIcon } from "lucide-react";
import { LogOut, ChevronDown, LayoutDashboard, Stethoscope, Settings, BookOpen, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { getNavIcon, HelpCircle } from "@/lib/navIcons";
import { prefetchRoute } from "@/lib/routePrefetch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useProfile } from "@/contexts/ProfileContext";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useVetOnlineStatus } from "@/hooks/useVetOnlineStatus";
import { DashboardTooltip } from "@/components/ui/dashboard-tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type NavItem = {
  to: string;
  label: string;
  /** Optional explicit icon. When omitted, resolved from NAV_ICON_REGISTRY by route. */
  icon?: LucideIcon;
  badge?: string;
  /** Highlight as a brand product (gradient text). */
  brand?: boolean;
  /** Group children under a dropdown. When set, `to` is ignored for navigation. */
  children?: NavItem[];
};

const activeItemClasses =
  "bg-primary/15 text-primary font-medium focus:bg-primary/15 focus:text-primary dark:bg-primary dark:text-primary-foreground dark:focus:bg-primary dark:focus:text-primary-foreground";

const getNavItems = (role: string | null): NavItem[] => [
  { to: "/vet-directory", label: "Vet Directory" },
  { to: "/vet-map", label: "Vet Map" },
  { to: "/how-it-works", label: "How It Works" },
  {
    to: "#services",
    label: "Services",
    children: [
      {
        to: "/symptom-sniffer",
        label: role === "vet" ? "Symptom Sniffer Pro" : "Symptom Sniffer",
      },
      { to: "/getvideovet", label: "GetVideoVet", brand: true },
      { to: "/getpetpassport", label: "GetPetPassport", brand: true },
    ],
  },
];

const NavLinks = ({
  role,
  isActivePath,
}: {
  role: string | null;
  isActivePath: (path: string) => boolean;
}) => {
  const items = getNavItems(role);

  return (
    <div className="hidden lg:flex items-center gap-0 xl:gap-0.5 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none [&>a]:pointer-events-auto [&>div]:pointer-events-auto [&>button]:pointer-events-auto">
      {items.map((item) => {
        const { to, label, icon, children } = item;

        if (children && children.length > 0) {
          const childActive = children.some((c) => isActivePath(c.to));
          const Icon = getNavIcon(to, icon) ?? HelpCircle;
          return (
            <DropdownMenu key={label}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-active={childActive ? "true" : undefined}
                  onMouseEnter={() => children.forEach((c) => prefetchRoute(c.to))}
                  className={cn(
                    "group relative inline-flex items-center gap-1 xl:gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-[13px] xl:px-3.5 xl:text-sm font-semibold tracking-tight transition-all duration-200",
                    "text-foreground/80 hover:text-foreground hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    childActive &&
                      "bg-primary/15 text-primary ring-1 ring-primary/40 shadow-sm dark:bg-primary dark:ring-primary dark:text-primary-foreground",
                  )}
                >
                  <Icon
                    className={cn(
                      "w-4 h-4 transition-transform duration-200 group-hover:-translate-y-0.5",
                      childActive
                        ? "text-primary dark:text-primary-foreground"
                        : "text-muted-foreground group-hover:text-primary",
                    )}
                    strokeWidth={2.25}
                  />
                  <span className={cn(childActive && "text-primary dark:text-primary-foreground")}>{label}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                {children.map((child) => {
                  const ChildIcon = getNavIcon(child.to, child.icon) ?? HelpCircle;
                  const cActive = isActivePath(child.to);
                  return (
                    <DropdownMenuItem
                      key={child.to}
                      asChild
                      className="focus:bg-primary/10 focus:text-primary dark:focus:bg-primary dark:focus:text-primary-foreground"
                    >
                      <Link
                        to={child.to}
                        aria-current={cActive ? "page" : undefined}
                        className={cn("cursor-pointer", cActive && activeItemClasses)}
                      >
                        <ChildIcon className="w-4 h-4 mr-2 text-muted-foreground" strokeWidth={2.25} />
                        {child.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        const Icon = getNavIcon(to, icon) ?? HelpCircle;
        const active = to === "/" ? false : isActivePath(to);
        return (
          <Link
            key={to}
            to={to}
            onMouseEnter={() => prefetchRoute(to)}
            onFocus={() => prefetchRoute(to)}
            aria-current={active ? "page" : undefined}
            data-active={active ? "true" : undefined}
            className={cn(
              "group relative inline-flex items-center gap-1 xl:gap-1.5 whitespace-nowrap rounded-full px-2 py-1.5 text-[13px] xl:px-3.5 xl:text-sm font-semibold tracking-tight transition-all duration-200",
              "text-foreground/80 hover:text-foreground hover:bg-primary/5",
              active &&
                "bg-primary/15 text-primary ring-1 ring-primary/40 shadow-sm hover:bg-primary/15 hover:text-primary dark:bg-primary dark:ring-primary dark:text-primary-foreground dark:shadow-[0_0_0_1px_hsl(var(--primary)/0.55)] dark:hover:bg-primary dark:hover:text-primary-foreground",
            )}
          >
            <Icon
              className={cn(
                "w-4 h-4 transition-transform duration-200 group-hover:-translate-y-0.5",
                active
                  ? "text-primary dark:text-primary-foreground"
                  : "text-muted-foreground group-hover:text-primary",
              )}
              strokeWidth={2.25}
            />
            <span
              className={cn(
                active && "text-primary dark:text-primary-foreground",
              )}
            >
              {label}
            </span>
            {!active && (
              <span
                className="pointer-events-none absolute inset-x-3 -bottom-1 h-0.5 rounded-full bg-gradient-to-r from-primary to-primary/40 origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100"
                aria-hidden
              />
            )}
          </Link>
        );
      })}
    </div>
  );
};

const MobileNav = ({
  role,
  isActivePath,
  isAuthed,
}: {
  role: string | null;
  isActivePath: (path: string) => boolean;
  isAuthed: boolean;
}) => {
  const items = getNavItems(role);
  const navListRef = useRef<HTMLUListElement>(null);

  /**
   * Roving keyboard navigation between mobile menu items.
   * Arrow Up/Down → previous/next item
   * Home / End    → first / last item
   * (Tab still works as expected via Radix focus trap; ESC closes the sheet.)
   */
  const handleListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const list = navListRef.current;
    if (!list) return;
    const links = Array.from(
      list.querySelectorAll<HTMLAnchorElement>('a[data-mobile-nav-item="true"]'),
    );
    if (links.length === 0) return;
    const currentIndex = links.findIndex((el) => el === document.activeElement);
    let nextIndex: number | null = null;
    switch (e.key) {
      case "ArrowDown":
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % links.length;
        break;
      case "ArrowUp":
        nextIndex = currentIndex < 0 ? links.length - 1 : (currentIndex - 1 + links.length) % links.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = links.length - 1;
        break;
      default:
        return;
    }
    if (nextIndex !== null) {
      e.preventDefault();
      links[nextIndex].focus();
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-9 w-9 focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open navigation menu"
          aria-haspopup="dialog"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[85%] max-w-sm p-0 flex flex-col"
        aria-labelledby="mobile-nav-title"
      >
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-border">
          <SheetTitle
            id="mobile-nav-title"
            className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground"
          >
            Menu
          </SheetTitle>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Primary">
          <ul
            ref={navListRef}
            onKeyDown={handleListKeyDown}
            className="flex flex-col gap-0.5"
          >
            {items.flatMap((item) => {
              if (item.children && item.children.length > 0) {
                return [
                  <li key={`group-${item.label}`} className="px-3 pt-3 pb-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {item.label}
                    </p>
                  </li>,
                  ...item.children.map((child) => {
                    const Icon = getNavIcon(child.to, child.icon) ?? HelpCircle;
                    const active = isActivePath(child.to);
                    return (
                      <li key={child.to}>
                        <SheetClose asChild>
                          <Link
                            to={child.to}
                            data-mobile-nav-item="true"
                            aria-current={active ? "page" : undefined}
                            aria-label={child.label}
                            className={cn(
                              "group flex items-center gap-2.5 rounded-full px-3 py-2.5 text-sm font-semibold tracking-tight transition-colors",
                              "text-foreground/80 hover:bg-primary/5 hover:text-foreground",
                              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                              active &&
                                "bg-primary/15 text-primary ring-1 ring-primary/40 dark:bg-primary dark:ring-primary dark:text-primary-foreground",
                            )}
                          >
                            <Icon
                              className={cn(
                                "w-4 h-4 shrink-0 transition-colors",
                                active
                                  ? "text-primary dark:text-primary-foreground"
                                  : "text-muted-foreground group-hover:text-primary",
                              )}
                              strokeWidth={2.25}
                              aria-hidden="true"
                            />
                            <span className={cn(active && "text-primary dark:text-primary-foreground")}>
                              {child.label}
                            </span>
                          </Link>
                        </SheetClose>
                      </li>
                    );
                  }),
                ];
              }
              const { to, label, icon } = item;
              const Icon = getNavIcon(to, icon) ?? HelpCircle;
              const active = to === "/" ? false : isActivePath(to);
              return [
                <li key={to}>
                  <SheetClose asChild>
                    <Link
                      to={to}
                      data-mobile-nav-item="true"
                      aria-current={active ? "page" : undefined}
                      aria-label={label}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-full px-3 py-2.5 text-sm font-semibold tracking-tight transition-colors",
                        "text-foreground/80 hover:bg-primary/5 hover:text-foreground",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                        active &&
                          "bg-primary/15 text-primary ring-1 ring-primary/40 dark:bg-primary dark:ring-primary dark:text-primary-foreground",
                      )}
                    >
                      <Icon
                        className={cn(
                          "w-4 h-4 shrink-0 transition-colors",
                          active
                            ? "text-primary dark:text-primary-foreground"
                            : "text-muted-foreground group-hover:text-primary",
                        )}
                        strokeWidth={2.25}
                        aria-hidden="true"
                      />
                      <span className={cn(active && "text-primary dark:text-primary-foreground")}>
                        {label}
                      </span>
                    </Link>
                  </SheetClose>
                </li>,
              ];
            })}
          </ul>
        </nav>
        {!isAuthed && (
          <div
            className="border-t border-border px-4 py-4 flex flex-col gap-2"
            role="group"
            aria-label="Account"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-1">
              Account
            </p>
            <SheetClose asChild>
              <Link to="/login" className="w-full">
                <Button variant="outline" size="sm" className="w-full rounded-full px-5">Log In</Button>
              </Link>
            </SheetClose>
            <SheetClose asChild>
              <Link to="/signup" className="w-full">
                <Button size="sm" className="w-full rounded-full px-5">Get Started</Button>
              </Link>
            </SheetClose>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Navbar = () => {
  const { user, loading, signOut } = useAuth();
  const { role } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const isActivePath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const { profile, loading: profileLoading } = useProfile();
  const [practiceName, setPracticeName] = useState<string | null>(null);
  const [practiceLogo, setPracticeLogo] = useState<string | null>(null);

  useEffect(() => {
    if (!user || role !== "vet") {
      setPracticeName(null);
      setPracticeLogo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("vet_practices")
        .select("name, logo_url")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!cancelled) {
        setPracticeName(data?.name?.trim() || null);
        setPracticeLogo(data?.logo_url || null);
      }
    })();
    return () => { cancelled = true; };
  }, [user, role]);

  const handleSignOut = async () => {
    await signOut("Navbar:userMenu", "User clicked Sign Out in navbar dropdown");
    toast.success("Signed out successfully");
    navigate("/");
  };

  const dashboardPath =
    role === "admin" ? "/admin" : role === "vet" ? "/vet-dashboard/snapshot" : "/my-dashboard";

  // Avoid flashing the email prefix before the profile resolves.
  const personalName =
    profile.display_name?.trim() ||
    (profileLoading ? "" : user?.email?.split("@")[0]) ||
    "Account";
  const displayName = role === "vet" && practiceName ? practiceName : personalName;
  const avatarSrc = role === "vet" ? (practiceLogo || profile.avatar_url) : profile.avatar_url;
  const initials =
    displayName
      .split(/\s+/)
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  const { isOnline: vetIsOnline } = useVetOnlineStatus(user?.id, role === "vet");
  const { sizes: logoSizes } = useLogoSizes();
  const navLogoH = useNavLogoHeight();

  return (
    <>
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      Skip to main content
    </a>
    <nav className="relative flex items-center justify-between px-6 py-4 h-20 bg-background border-b border-border">
      <Link
        to="/"
        onClick={() => {
          try { sessionStorage.removeItem("find-vets:all-published:v1"); } catch { /* ignore */ }
          window.dispatchEvent(new CustomEvent("getpetvet:reset-home"));
        }}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-10 flex items-center leading-none"
        aria-label="GetPetVet home - The best care yet"
      >
        <span className="sr-only">GetPetVet</span>
        <GetPetVetLogo
          aria-hidden="true"
          style={{ height: navLogoH, width: navLogoH * (2054 / 496), aspectRatio: "2054 / 496" }}
          className="block max-w-none text-foreground"
        />
      </Link>
      {/* Spacer to reserve horizontal space for the absolutely-positioned logo */}
      <div aria-hidden="true" style={{ width: navLogoH * (2054 / 496) }} className="shrink-0" />

      <NavLinks role={role} isActivePath={isActivePath} />


      <div className="flex items-center gap-2 sm:gap-3">
        <MobileNav role={role} isActivePath={isActivePath} isAuthed={!!user} />
        {loading ? null : user ? (
          <>
            <div className="flex items-center gap-0.5">
              <MessageBell />
              <NotificationBell />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" aria-label="Account menu" className="rounded-full pl-1 pr-3 h-9 gap-2 hover:bg-muted">
                  <span className="relative inline-flex">
                    <Avatar className={cn("w-7 h-7", vetIsOnline && "ring-2 ring-emerald-500 ring-offset-1 ring-offset-background")}>
                      {avatarSrc && (
                        <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
                      )}
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    {vetIsOnline && (
                      <DashboardTooltip
                        label="You're live on GetVideoVet - pet owners can request a consult."
                        className="text-xs"
                      >
                        <span
                          aria-label="Online for GetVideoVet"
                          className="absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5"
                        >
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                        </span>
                      </DashboardTooltip>
                    )}
                  </span>
                  <span className="hidden sm:inline max-w-[120px] truncate text-sm font-medium">
                    {displayName}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="flex items-center gap-3 px-2 py-2">
                  <span className="relative inline-flex shrink-0">
                    <Avatar className={cn("w-9 h-9", vetIsOnline && "ring-2 ring-emerald-500 ring-offset-1 ring-offset-background")}>
                      {avatarSrc && (
                        <AvatarImage src={avatarSrc} alt={displayName} className="object-cover" />
                      )}
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    {vetIsOnline && (
                      <span
                        aria-hidden
                        className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-popover"
                      />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                    {role === "vet" && practiceName ? (
                      <p className="text-xs text-muted-foreground truncate">{personalName}</p>
                    ) : user.email ? (
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    ) : null}
                    {vetIsOnline && (
                      <Link
                        to="/vet-dashboard#available-now"
                        className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15 transition-colors"
                      >
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-70" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        </span>
                        Live on GetVideoVet
                      </Link>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate(dashboardPath)}
                  aria-current={isActivePath(dashboardPath) ? "page" : undefined}
                  className={isActivePath(dashboardPath) ? activeItemClasses : undefined}
                >
                  {role === "vet" ? <Stethoscope className="w-4 h-4 mr-2" /> : <LayoutDashboard className="w-4 h-4 mr-2" />}
                  {role === "admin" ? "Admin Dashboard" : role === "vet" ? "Vet Dashboard" : "Dashboard"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/helpdesk")}
                  aria-current={isActivePath("/helpdesk") ? "page" : undefined}
                  className={isActivePath("/helpdesk") ? activeItemClasses : undefined}
                >
                  <BookOpen className="w-4 h-4 mr-2" /> Helpdesk
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  aria-current={isActivePath("/settings") ? "page" : undefined}
                  className={isActivePath("/settings") ? activeItemClasses : undefined}
                >
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <div className="hidden sm:flex items-center gap-2">
            <Link to="/login">
              <Button variant="outline" size="sm" className="rounded-full px-5">Log In</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm" className="rounded-full px-5">Get Started</Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;
